import type { Profile, ProfileMeasurements, SizeHistoryEntry } from "../types";

export const LS_PREFIX = "primestyle_";
const PROFILES_KEY = "profiles";
const ACTIVE_PROFILE_KEY = "active_profile_id";

/** Custom event name fired on `window` whenever profile/active/cache state
 *  changes. Used by usePrimeStyleSize to refetch in real time. */
export const PS_STORAGE_CHANGE_EVENT = "ps:storage-change";

function emitStorageChange(reason: string): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(PS_STORAGE_CHANGE_EVENT, { detail: { reason } }));
  } catch {
    // dispatch might fail in very old browsers — silent fail
  }
}

export function lsGet<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

export function lsSet(key: string, value: unknown): void {
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(value));
  } catch {
    // quota exceeded — silent fail
  }
}

export function lsRemove(key: string): void {
  try {
    localStorage.removeItem(LS_PREFIX + key);
  } catch {
    // silent fail
  }
}

export function clearProfileLocalStorage(): void {
  lsRemove(PROFILES_KEY);
  lsRemove(ACTIVE_PROFILE_KEY);
  lsRemove("profile_completion_draft");
  try {
    localStorage.removeItem(PROFILES_KEY);
    localStorage.removeItem(ACTIVE_PROFILE_KEY);
    localStorage.removeItem("profile_completion_draft");
  } catch {
    // silent fail
  }
  emitStorageChange("profile-clear");
}

// ─────────────────────────────────────────────────────────────────────
// Profile helpers — single source of truth for the active profile and
// its size history. Used by both the SDK modal AND the headless
// recommendForProduct() function.
// ─────────────────────────────────────────────────────────────────────

export function getProfiles(): Profile[] {
  return lsGet<Profile[]>(PROFILES_KEY, []);
}

export function saveProfiles(profiles: Profile[]): void {
  lsSet(PROFILES_KEY, profiles);
  emitStorageChange("profiles");
}

export function getActiveProfileId(): string | null {
  return lsGet<string | null>(ACTIVE_PROFILE_KEY, null);
}

export function isActiveProfileExplicitlyCleared(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(LS_PREFIX + ACTIVE_PROFILE_KEY) === "null";
  } catch {
    return false;
  }
}

export function setActiveProfileId(id: string | null): void {
  lsSet(ACTIVE_PROFILE_KEY, id);
  emitStorageChange("active-profile");
}

/** Returns the active profile, or the most recently used one, or null. */
export function getActiveProfile(): Profile | null {
  const profiles = getProfiles();
  if (profiles.length === 0) return null;
  const activeId = getActiveProfileId();
  if (activeId) {
    const found = profiles.find((p) => p.id === activeId);
    if (found) return found;
  }
  // Fallback: most recently used
  const sorted = [...profiles].sort(
    (a, b) => (b.lastUsedAt || b.createdAt || 0) - (a.lastUsedAt || a.createdAt || 0)
  );
  return sorted[0] || null;
}

/** Mutate a single profile in storage by id. */
export function updateProfile(id: string, patch: Partial<Profile>): Profile | null {
  const profiles = getProfiles();
  const idx = profiles.findIndex((p) => p.id === id);
  if (idx < 0) return null;
  const updated = { ...profiles[idx], ...patch, lastEditedAt: Date.now() } as Profile;
  profiles[idx] = updated;
  saveProfiles(profiles); // emits "profiles"
  return updated;
}

/** Save the LLM-computed body measurements onto a profile. */
export function updateProfileMeasurements(
  id: string,
  measurements: ProfileMeasurements,
  unit: "cm" | "in" = "cm"
): Profile | null {
  return updateProfile(id, { measurements, measurementsUnit: unit });
}

/** Append (or replace) a per-product size recommendation in the profile's history. */
export function addSizeToHistory(
  profileId: string,
  entry: SizeHistoryEntry
): Profile | null {
  const profiles = getProfiles();
  const idx = profiles.findIndex((p) => p.id === profileId);
  if (idx < 0) return null;
  const profile = profiles[idx];
  const history = (profile.sizeHistory || []).filter(
    (h) => h.productId !== entry.productId
  );
  history.unshift(entry);
  // Keep the last 50 to avoid quota issues
  const trimmed = history.slice(0, 50);
  profiles[idx] = {
    ...profile,
    sizeHistory: trimmed,
    lastUsedAt: Date.now(),
  };
  saveProfiles(profiles); // emits "profiles"
  return profiles[idx];
}

/** Look up a cached size recommendation for a specific product on a profile. */
export function getCachedSize(
  profile: Profile,
  productId: string
): SizeHistoryEntry | null {
  if (!profile.sizeHistory || profile.sizeHistory.length === 0) return null;
  const entry = profile.sizeHistory.find((h) => h.productId === productId);
  if (!entry) return null;
  // Invalidate if profile has been edited since this entry was saved
  if (profile.lastEditedAt && profile.lastEditedAt > entry.savedAt) return null;
  return entry;
}
